import { ReactElement, useState } from 'react';
import { signIn, useSession } from 'next-auth/react';
import Head from 'next/head';
import SVG from 'react-inlinesvg';
import { IWithInitialProps } from 'typings/IWithInitialProps';
import {
  ConnectWrapperStyle,
  ConnectSuccessScreenStyle,
  ConnectTopSectionStyle,
  ConnectLeftSectionStyle,
  ConnectRightSectionStyle,
  ConnectSmartTvImageStyle,
  ConnectLoggedOutStyle,
  ConnectLoggedInStyle,
  ConnectInputWrapperStyle,
  ConnectWrongCodeStyle
} from 'components/Connect/Connect.style';
import { AboutMoreCornerGradientStyle } from 'components/About/About.style';
import Button from 'components/Button';
import PinInput from 'components/PinInput';
import { Heading1, Heading3, BodyText } from 'components/Text/Text.style';
import { LayoutStyle } from 'components/Layout/Layout.style';
import { getStaticAsset } from 'lib/helpers/resources';

import { sendUserCode } from 'lib/helpers/sendUserCode';
import { trackClick } from 'lib/tealium';
import { Grid } from '../src/client/styles/grid';
import {
  IS_CLIENT,
  ICONS,
  TEALIUM_EVENT_NAME,
  TEALIUM_EVENT_CATEGORY,
  TEALIUM_EVENT_LABEL,
  TEALIUM_CODE_LABEL,
  USER_CODE_RESPONSE,
  CODE_ERROR_MESSAGE
} from '../src/client/constants';

const Connect: IWithInitialProps<{ eventDeviceActivation: string }> = ({ eventDeviceActivation }): ReactElement<{}> => {
  const [errorMessage, setErrorMessage] = useState('');
  const [isSuccessful, setIsSuccessful] = useState(false);
  const [code, setCode] = useState<string>(() => {
    if (!IS_CLIENT) return '';
    const [, prefillCode] = window.location.search.match(/code=([^&]*)/) || [];
    return prefillCode ? prefillCode.replace('-', '') : '';
  });

  const { status } = useSession();

  const isAuthenticated = status === 'authenticated';
  const isLoadingUser = status === 'loading';

  const sendCode = () => {
    sendUserCode(code, eventDeviceActivation, (success, errorMessage) => {
      trackClick({
        name: TEALIUM_EVENT_NAME.CONNECT_SUBMIT,
        category: TEALIUM_EVENT_CATEGORY.CONNECT,
        label:
          success || !errorMessage
            ? TEALIUM_EVENT_LABEL.SUCCESS
            : TEALIUM_CODE_LABEL[errorMessage] || TEALIUM_EVENT_LABEL.NOT_FOUND
      });

      if (errorMessage && !success) {
        setErrorMessage(CODE_ERROR_MESSAGE[errorMessage] || USER_CODE_RESPONSE.DEFAULT);
        return;
      }

      setIsSuccessful(true);
      window.scrollTo(0, 0);
    });
  };

  const handleInputChange = (pinResult: string) => {
    setCode(pinResult);
    setErrorMessage('');
  };

  return (
    <>
      <Head>
        <title>Connect - Je ziet meer met KIJK.nl</title>
      </Head>
      <ConnectWrapperStyle data-testid="connect">
        <AboutMoreCornerGradientStyle />
        <LayoutStyle data-testid="layoutStyle">
          {!isSuccessful && (
            <div>
              <Grid gridSmRowGap={'24px'} gridMdRowGap={'36px'} gridLgRowGap={'64px'}>
                <ConnectTopSectionStyle>
                  <Heading1>Beleef KIJK groots met de KIJK TV app</Heading1>
                  <BodyText>
                    Kijken naar al jouw favoriete programma’s van SBS6, Net5, Veronica en SBS9 wanneer het jou uitkomt
                    doe je nu in de vernieuwde KIJK TV app!
                  </BodyText>
                </ConnectTopSectionStyle>
                <ConnectLeftSectionStyle>
                  <ConnectSmartTvImageStyle>
                    <img src={getStaticAsset('static/images/smart-tv-02.png')} alt="smart-tv" />
                  </ConnectSmartTvImageStyle>
                </ConnectLeftSectionStyle>
                <ConnectRightSectionStyle data-testid="connectRightSectionStyle">
                  <Heading1>Koppel nieuw apparaat</Heading1>
                  <BodyText>
                    Om in te loggen in de KIJK TV app vragen we jou, je KIJK-account te koppelen. Voer hieronder de KIJK
                    code in die zichtbaar is op het andere apparaat.
                  </BodyText>
                  {isAuthenticated ? (
                    <ConnectLoggedInStyle data-testid="connectLoggedInStyle">
                      {errorMessage && <ConnectWrongCodeStyle>{errorMessage}</ConnectWrongCodeStyle>}
                      <Heading3>Je KIJK code</Heading3>
                      <ConnectInputWrapperStyle isError={errorMessage}>
                        <PinInput initialValue={code} isError={!!errorMessage} onChange={handleInputChange} />
                      </ConnectInputWrapperStyle>
                      <Button onClick={sendCode} isActionButton>
                        Koppel apparaat
                      </Button>
                    </ConnectLoggedInStyle>
                  ) : !isLoadingUser ? (
                    <ConnectLoggedOutStyle data-testid="connectLoggedOutStyle">
                      <Button onClick={() => signIn('talpaauth')} isActionButton>
                        Inloggen
                      </Button>
                    </ConnectLoggedOutStyle>
                  ) : null}
                </ConnectRightSectionStyle>
              </Grid>
            </div>
          )}
          {isSuccessful && (
            <ConnectSuccessScreenStyle>
              <Grid gridSmRowGap={'24px'} gridMdRowGap={'36px'} gridLgRowGap={'64px'}>
                <ConnectTopSectionStyle>
                  <Heading1>Code juist ingevoerd!</Heading1>
                  <SVG src={ICONS.CHECK_CIRCULAR} />
                  <BodyText>
                    Je KIJK-account is gekoppeld.
                    <br />
                    Ga terug naar je Smart TV en controleer de KIJK code.
                  </BodyText>
                  <Button
                    onClick={() => {
                      setIsSuccessful(false);
                      setCode('');
                    }}
                  >
                    Koppel nog een apparaat
                  </Button>
                </ConnectTopSectionStyle>
              </Grid>
            </ConnectSuccessScreenStyle>
          )}
        </LayoutStyle>
      </ConnectWrapperStyle>
    </>
  );
};

Connect.getInitialProps = async (): Promise<any> => ({ eventDeviceActivation: process.env.DEVICE_ACTIVATION_ENDPOINT });

export default Connect;
